/** Threshold, status and colour rules. */
import type { ClusterModel } from './cluster-model.ts';
import type { AlertRule, StatusRule, OrgSettings } from './settings.ts';

export type Status = 'ok' | 'warn' | 'alert';
export type GaugeState = Status | 'stale' | 'none';

export function evaluateStatus(value: number, rule: StatusRule | undefined): Status | 'none' {
  if (!rule || !Number.isFinite(value)) return 'none';
  if (rule.direction === 'higher_is_better') {
    if (value <= rule.red_at) return 'alert';
    if (value <= rule.amber_at) return 'warn';
    return 'ok';
  }
  if (value >= rule.red_at) return 'alert';
  if (value >= rule.amber_at) return 'warn';
  return 'ok';
}

/** True when the alert threshold is crossed (strictly above / below). */
export function isAlert(value: number, rule: Pick<AlertRule, 'when' | 'threshold'> | undefined): boolean {
  if (!rule || !Number.isFinite(value)) return false;
  return rule.when === 'above' ? value > rule.threshold : value < rule.threshold;
}

const SEVERITY: Record<GaugeState, number> = { none: 0, ok: 1, warn: 2, alert: 3, stale: 4 };

/** Worst state wins; stale outranks everything so missing data is never shown as "green". */
export function worstState(states: GaugeState[]): GaugeState {
  return states.reduce<GaugeState>((worst, s) => (SEVERITY[s] > SEVERITY[worst] ? s : worst), 'none');
}

export function colorForState(state: GaugeState, colors: OrgSettings['colors']): string {
  if (state === 'none') return colors.neutral;
  return colors[state];
}

/**
 * Backlight colour per zone.
 * - fixed mode: every zone shows the report set's fixed colour (stale still overrides, so a frozen display is visible);
 * - status mode: each zone shows the worst state of the gauges linked to it.
 */
export function zoneColors(
  model: Pick<ClusterModel, 'backlight_zones'>,
  states: Record<string, GaugeState>,
  mode: { colorMode: 'status' | 'fixed'; fixedColor?: string | null },
  colors: OrgSettings['colors'],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const zone of model.backlight_zones) {
    const zoneStates = zone.gauges.map((g) => states[g] ?? 'none');
    const worst = worstState(zoneStates);
    if (worst === 'stale') out[zone.id] = colors.stale;
    else if (mode.colorMode === 'fixed') out[zone.id] = mode.fixedColor ?? colors.neutral;
    else out[zone.id] = colorForState(worst, colors);
  }
  return out;
}
